import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { cardTypeOptions } from "@/common/constants/cards.constant";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useTableSelect from "@/common/hooks/useTableSelect";
import { Mapper } from "@/common/services/mapper";
import { Option } from "@/common/types/option";
import MarkCardsAsShipped from "@/features/cards/components/pendingCards/MarkCardsAsShipped";
import ValidateCardsPopover from "@/features/cards/components/validateCards/ValidateCardsPopover";
import { pendingCardsDefaultColumns } from "@/features/cards/constants/pendingCardsDefaultColumns.constant";
import { pendingCardStatusOptions } from "@/features/cards/constants/pendingCardStatus.constant";
import { useCardsInFabrication } from "@/features/cards/hooks/useCardsInFabrication";
import { PendingCardsTransformer } from "@/features/cards/services/pendingCardsTransformer";
import { useCompanies } from "@/features/companies/hooks/useCompanies";
import { CompaniesTransformer } from "@/features/companies/services/companiesTransformer";
import { CreditCard, Search } from "lucide-react";

const PendingCardsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();
  const { date } = useFilterDate(filterParams);

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchCompany, setSearchCompany] = useDebounce('');

  const tableSelect = useTableSelect('id');

  const { data: companiesOptions } = useCompanies({
    filters: { search: searchCompany },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  const {
    data: cards,
    meta,
    isPending: cardsIsPending,
    isLoading: cardsIsLoading,
    error: cardsError = {} as any,
    refetch: refetchCards,
  } = useCardsInFabrication({
    filters: {
      type: filterParams.type?.value,
      company_id: filterParams.company?.value,
      search,
      status: filterParams.status?.value,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer({ transformer: PendingCardsTransformer.tableTransformer() })
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <ListHeader
          Icon={<CreditCard className="w-10 h-10" strokeWidth={1} />}
          title="Cartões pendentes"
        />

        {!(cardsIsPending || cardsError?.status === 403) && (
          <div className="flex items-center gap-3">
            <MarkCardsAsShipped ids={tableSelect.selectedItems} clearSelectedItems={tableSelect.clearSelectedItems} />

            <ValidateCardsPopover />
          </div>
        )}
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={cardsError?.status}
        refetchFunction={refetchCards}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<Search />}
              placeholder="Número do cartão"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center justify-start gap-2">
              <DatePickerWithRange date={date} setDate={(date) => setFilterParams((prev) => ({ ...prev, date, page: 1 }))} />

              <ListFilters
                filters={[
                  {
                    className: '',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, type: e, page: 1 })),
                    value: filterParams.type,
                    options: [{ label: 'Todas', value: '' }, ...cardTypeOptions],
                    placeholder: 'Tipo'
                  },
                  {
                    className: '',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, company: e, page: 1 })),
                    onInputChange: setSearchCompany,
                    value: filterParams.company,
                    options: [{ label: 'Todas', value: '' }, ...companiesOptions as Option[]],
                    placeholder: 'Empresa'
                  },
                  {
                    className: '',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e, page: 1 })),
                    value: filterParams.status,
                    options: [{ label: 'Todos', value: '' }, ...pendingCardStatusOptions],
                    placeholder: 'Status'
                  },
                ]}
              />
            </div>
          </div>

          <Table
            columns={pendingCardsDefaultColumns}
            isLoadingData={cardsIsLoading}
            page={Number(filterParams.page) || 1}
            rows={cards}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
            tableSelect={tableSelect}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default PendingCardsList;
